import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  LinearProgress,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import {
  CheckRounded,
  CloseRounded,
  CloudUpload as CloudUploadIcon,
  GetApp as GetAppIcon,
  PlayArrow as PlayArrowIcon,
} from "@material-ui/icons";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { agregarLibroFila } from "../../../../shared/fetchers/filasHome";

const PLANTILLA_HEADER = "idLibro";
const ACCEPTED = ".csv,.xlsx,.xls";

/**
 * Parsea un archivo CSV/XLS/XLSX a una matriz de strings.
 * Toma la primera columna como id de libro.
 */
const parseFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    blankrows: false,
    defval: "",
  });

  const ids = [];
  rows.forEach((row, i) => {
    if (!row || row.length === 0) return;
    const raw = row[0];
    const value = raw === null || raw === undefined ? "" : String(raw).trim();
    if (!value) return;
    if (i === 0 && value.toLowerCase().replace(/\s+/g, "") === PLANTILLA_HEADER.toLowerCase()) {
      return;
    }
    ids.push(value);
  });

  return ids;
};

const downloadCsvTemplate = () => {
  const content = `${PLANTILLA_HEADER}\n12345\n67890\n`;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "plantilla-libros-fila.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ImportarLibrosMasivo = ({
  filaHome,
  libros = [],
  librosFila = [],
  onClose,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const [fileName, setFileName] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [resultSummary, setResultSummary] = useState(null);

  const librosMap = useMemo(() => {
    const map = new Map();
    libros.forEach((l) => {
      const id = String(l.idLibro);
      map.set(id, l);
    });
    return map;
  }, [libros]);

  const yaEnFilaSet = useMemo(
    () => new Set(librosFila.map((l) => String(l.idLibro))),
    [librosFila]
  );

  const validRows = useMemo(
    () => parsedRows.filter((r) => r.status === "valido"),
    [parsedRows]
  );

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setResultSummary(null);
    setProgress({ done: 0, total: 0 });

    try {
      const ids = await parseFile(file);
      if (!ids.length) {
        toast.error("El archivo no contiene IDs válidos");
        setParsedRows([]);
        return;
      }

      const seen = new Set();
      const rows = ids.map((id) => {
        const idLibro = id;
        const libro = librosMap.get(String(id));
        let status = "valido";
        let mensaje = "Listo para importar";

        if (seen.has(idLibro)) {
          status = "duplicado";
          mensaje = "Repetido en el archivo";
        } else if (!libro) {
          status = "no_encontrado";
          mensaje = "ID no existe en el catálogo";
        } else if (yaEnFilaSet.has(idLibro)) {
          status = "ya_existente";
          mensaje = "Ya pertenece a la fila";
        }

        seen.add(idLibro);
        return {
          idLibro,
          title: libro?.title ?? "—",
          status,
          mensaje,
        };
      });

      setParsedRows(rows);
    } catch (error) {
      console.error(error);
      toast.error(
        "No se pudo leer el archivo. Verifica el formato (CSV/Excel)."
      );
      setParsedRows([]);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const reset = () => {
    setFileName(null);
    setParsedRows([]);
    setResultSummary(null);
    setProgress({ done: 0, total: 0 });
  };

  const importar = async () => {
    if (!filaHome) {
      toast.error("Selecciona una fila destino");
      return;
    }
    if (!validRows.length) {
      toast.error("No hay filas válidas para importar");
      return;
    }

    setImporting(true);
    setProgress({ done: 0, total: validRows.length });

    let ok = 0;
    let failed = 0;
    const failedDetail = [];

    for (let i = 0; i < validRows.length; i += 1) {
      const row = validRows[i];
      try {
        const res = await agregarLibroFila({
          idLibro: row.idLibro,
          nombreFila: filaHome,
        });
        if (res?.status) ok += 1;
        else {
          failed += 1;
          failedDetail.push({ idLibro: row.idLibro, info: res?.info });
        }
      } catch (error) {
        failed += 1;
        failedDetail.push({ idLibro: row.idLibro, info: error?.message });
      }
      setProgress({ done: i + 1, total: validRows.length });
    }

    queryClient.invalidateQueries({ queryKey: ["librosFila", filaHome] });
    queryClient.invalidateQueries({ queryKey: ["filasHome"] });

    setResultSummary({ ok, failed, failedDetail });
    setImporting(false);

    if (failed === 0) {
      toast.success(`Se importaron ${ok} libros a la fila`);
    } else if (ok === 0) {
      toast.error("No se pudo importar ningún libro");
    } else {
      toast(`Importación parcial: ${ok} ok / ${failed} con error`, {
        icon: "⚠️",
      });
    }
  };

  const renderStatusChip = (status) => {
    const map = {
      valido: { label: "Válido", color: "#5fbeaa" },
      duplicado: { label: "Duplicado", color: "#ff9800" },
      no_encontrado: { label: "No encontrado", color: "#dc3545" },
      ya_existente: { label: "Ya en la fila", color: "#6c757d" },
    };
    const meta = map[status] ?? map.valido;
    return (
      <span
        className={classes.badge}
        style={{ backgroundColor: meta.color }}
      >
        {meta.label}
      </span>
    );
  };

  const summary = useMemo(() => {
    const counts = parsedRows.reduce(
      (acc, r) => {
        acc[r.status] = (acc[r.status] ?? 0) + 1;
        return acc;
      },
      { valido: 0, duplicado: 0, no_encontrado: 0, ya_existente: 0 }
    );
    return { ...counts, total: parsedRows.length };
  }, [parsedRows]);

  return (
    <div className={classes.wrapper}>
      <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
        <div>
          <div className="admin-main-title-dark-gray">
            Importación masiva de libros
          </div>
          <div className="admin-muted-text-small">
            Sube un archivo CSV o Excel con una sola columna que contenga los
            IDs de libros que quieres agregar a la fila{" "}
            <strong>{filaHome ?? "—"}</strong>.
          </div>
        </div>
        {onClose && (
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            startIcon={<CloseRounded />}
          >
            Cerrar
          </Button>
        )}
      </div>

      <div className={classes.actionsBar}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<GetAppIcon />}
          onClick={downloadCsvTemplate}
        >
          Descargar plantilla CSV
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          classes={{ root: classes.uploadBtn }}
        >
          {fileName ? "Cambiar archivo" : "Seleccionar archivo"}
        </Button>

        {fileName && (
          <Tooltip title={fileName}>
            <Chip
              label={fileName}
              size="small"
              onDelete={importing ? undefined : reset}
            />
          </Tooltip>
        )}
      </div>

      {parsedRows.length > 0 && (
        <>
          <div className={classes.summaryRow}>
            <Chip
              size="small"
              label={`Total: ${summary.total}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Válidos: ${summary.valido}`}
              style={{ background: "#e6f7f1", color: "#2f8270" }}
            />
            {summary.ya_existente > 0 && (
              <Chip
                size="small"
                label={`Ya en la fila: ${summary.ya_existente}`}
                style={{ background: "#eee", color: "#555" }}
              />
            )}
            {summary.duplicado > 0 && (
              <Chip
                size="small"
                label={`Duplicados: ${summary.duplicado}`}
                style={{ background: "#fff3e0", color: "#cc7a00" }}
              />
            )}
            {summary.no_encontrado > 0 && (
              <Chip
                size="small"
                label={`No encontrados: ${summary.no_encontrado}`}
                style={{ background: "#fdecea", color: "#a8261e" }}
              />
            )}
          </div>

          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th style={{ width: 120 }}>ID Libro</th>
                  <th>Título</th>
                  <th style={{ width: 140 }}>Estado</th>
                  <th>Mensaje</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, idx) => (
                  <tr key={`${row.idLibro}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{row.idLibro}</td>
                    <td>{row.title}</td>
                    <td>{renderStatusChip(row.status)}</td>
                    <td className="admin-muted-text-small">{row.mensaje}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {importing && (
            <div className="my-3">
              <div className="admin-muted-text-small mb-1">
                Importando {progress.done} de {progress.total}…
              </div>
              <LinearProgress
                variant="determinate"
                value={
                  progress.total
                    ? (progress.done / progress.total) * 100
                    : 0
                }
              />
            </div>
          )}

          {resultSummary && (
            <div className={classes.resultBox}>
              <div>
                <strong>Resultado:</strong>{" "}
                <span style={{ color: "#2f8270" }}>
                  {resultSummary.ok} agregados
                </span>
                {" · "}
                <span style={{ color: "#a8261e" }}>
                  {resultSummary.failed} con error
                </span>
              </div>
              {resultSummary.failed > 0 && (
                <details className="mt-2">
                  <summary className="admin-muted-text-small">
                    Ver detalle de errores
                  </summary>
                  <ul className="mt-2 mb-0">
                    {resultSummary.failedDetail.map((f, i) => (
                      <li key={i} className="admin-muted-text-small">
                        ID {f.idLibro}: {f.info ?? "Error desconocido"}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div className="d-flex justify-content-end mt-3" style={{ gap: 12 }}>
            <Button
              variant="outlined"
              onClick={reset}
              disabled={importing}
              startIcon={<CloseRounded />}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              onClick={importar}
              disabled={importing || validRows.length === 0}
              startIcon={importing ? <PlayArrowIcon /> : <CheckRounded />}
              classes={{ root: classes.confirmBtn }}
            >
              {importing
                ? "Importando…"
                : `Confirmar e importar (${validRows.length})`}
            </Button>
          </div>
        </>
      )}

      {parsedRows.length === 0 && fileName == null && (
        <div className={classes.helpBox}>
          <div className="admin-muted-text-small">
            <strong>Formato esperado:</strong> una sola columna con IDs de
            libros (uno por fila). La primera fila puede ser un encabezado{" "}
            <code>{PLANTILLA_HEADER}</code> opcional.
          </div>
          <div className="admin-muted-text-small mt-1">
            Formatos soportados: <code>.csv</code>, <code>.xlsx</code>,{" "}
            <code>.xls</code>.
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportarLibrosMasivo;

const useStyles = makeStyles({
  wrapper: {
    background: "#fafafa",
    border: "1px solid #e8e8e8",
    borderRadius: 6,
    padding: "16px 18px",
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 10,
    marginBottom: 12,
  },
  uploadBtn: {
    background: "#5fbeaa",
    color: "#fff",
    "&:hover": { backgroundColor: "#4ea493" },
  },
  confirmBtn: {
    background: "#81c868",
    color: "#fff",
    "&:hover": { backgroundColor: "#6fb558" },
    "&.Mui-disabled": {
      background: "#cdeac3",
      color: "#fff",
    },
  },
  summaryRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  tableWrapper: {
    maxHeight: 300,
    overflow: "auto",
    border: "1px solid #e8e8e8",
    borderRadius: 4,
    background: "#fff",
  },
  table: {
    width: "100%",
    fontSize: "0.85em",
    borderCollapse: "collapse",
    "& th, & td": {
      padding: "6px 10px",
      borderBottom: "1px solid #f0f0f0",
      textAlign: "left",
    },
    "& thead th": {
      background: "#f5f5f5",
      fontWeight: 600,
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
  },
  badge: {
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: "0.75em",
    fontWeight: 500,
  },
  resultBox: {
    marginTop: 12,
    padding: 12,
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  helpBox: {
    background: "#fff",
    border: "1px dashed #cfcfcf",
    borderRadius: 4,
    padding: 14,
  },
});
